import { useState } from 'react';
import { isAxiosError } from 'axios';
import { createStudent, updateStudent } from '../../../api/students';
import { CreateStudentPayload, StudentListItem } from '../../../types/student';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to save') : 'Failed to save';

/** Persists the Student Application Wizard's progress to the backend as the
 * counselor advances through steps: creates the student as a Draft on Step 1,
 * then PATCHes on every subsequent step so the draft can be resumed later. */
export const useStudentDraftAutosave = () => {
  const [studentId, setStudentId] = useState<number | null>(null);
  const [applicationNumber, setApplicationNumber] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const saveStep = async (
    payload: CreateStudentPayload,
    lastCompletedStep: number,
    leadId?: number | null,
  ): Promise<StudentListItem | null> => {
    setSaving(true);
    setError(null);
    try {
      let student: StudentListItem;
      if (!studentId) {
        student = await createStudent({
          ...payload,
          saveAsDraft: true,
          lastCompletedStep,
          ...(leadId ? { leadId } : {}),
        });
      } else {
        student = await updateStudent(studentId, { ...payload, lastCompletedStep });
      }
      setStudentId(student.id);
      setApplicationNumber(student.applicationNumber);
      setSavedAt(new Date());
      return student;
    } catch (err) {
      setError(extractErrorMessage(err));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const hydrate = (student: StudentListItem) => {
    setStudentId(student.id);
    setApplicationNumber(student.applicationNumber);
    setSavedAt(null);
    setError(null);
  };

  const reset = () => {
    setStudentId(null);
    setApplicationNumber(null);
    setSavedAt(null);
    setError(null);
  };

  return { studentId, applicationNumber, saving, error, savedAt, saveStep, hydrate, reset };
};
